import React, { useEffect, useState } from 'react'
import "./SelfPayment.css"
import { useDispatch, useSelector } from 'react-redux'
import { setSelfOrderScreen } from '../Order/selfOrderEssentialsSlice'
import { setSelfSingleOrderInfo } from '../../Slices/selfOrderMainSlice'
import { completeSelfOrderAPI, payAtCounterSelfOrderAPI } from '../../API/selfOrderAPIS'
import { paymentTypesForBilling } from '../../../component/Modules/Sales/Billing/newBillingAPI'
import { formatCurrencyValue } from '../../SelfGeneralFunctions'
import { today, getCurrentTime } from '../../../Js/Date'
import counter from "./../../../Assets/SVG/counter.svg"
import payment from "./../../../Assets/SVG/payment-_2_.svg"
import CreditCardIcon from '@mui/icons-material/CreditCard'
import QrCodeIcon from '@mui/icons-material/QrCode'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import PaymentsIcon from '@mui/icons-material/Payments'
import { CircularProgress } from '@mui/material'

const SelfPayment = () => {
    const dispatch = useDispatch()
    const decimalPosition = localStorage.getItem("decimalPosition")
    const selfUser = localStorage.getItem("selfUser")
    const isStaff = selfUser === "STAFF"

    const { selfSingleOrderInfo } = useSelector((state) => state.selfOrderMainSlice)
    const { receiptData } = useSelector((state) => state.viewOrdersBillingSlice)
    const { currencyDetails, submitLoading } = useSelector((state) => state.mainDashBoardSlice)
    const { paymentTypeList } = useSelector((state) => state.newBillingSlice)
    const { defaultCustomer } = useSelector((state) => state.topBarSliceBilling)
    const userInfo = useSelector((state) => state.loginResponseSlice.value)
    const userBranchId = userInfo?.branchPk

    const [selectedPayment, setSelectedPayment] = useState(null)

    // Safety net: ensure configured payment methods are loaded for staff to choose
    useEffect(() => {
        if (userBranchId && (!paymentTypeList || paymentTypeList.length === 0)) {
            paymentTypesForBilling({ branchId: userBranchId })
        }
    }, [userBranchId])

    const backBtnFunction = () => {
        dispatch(setSelfOrderScreen("orderConfirmScreen"))
    }

    const getPaymentIcon = (journalName) => {
        const name = (journalName || '').toLowerCase()
        if (name.includes('card') || name.includes('credit') || name.includes('debit')) {
            return <CreditCardIcon sx={{ fontSize: 40, color: '#1976d2' }} />
        }
        if (name.includes('upi') || name.includes('qr') || name.includes('scan')) {
            return <QrCodeIcon sx={{ fontSize: 40, color: '#4caf50' }} />
        }
        if (name.includes('wallet') || name.includes('paytm') || name.includes('phonepe')) {
            return <AccountBalanceWalletIcon sx={{ fontSize: 40, color: '#ff9800' }} />
        }
        return <PaymentsIcon sx={{ fontSize: 40, color: '#9c27b0' }} />
    }

    const handlePaymentSelect = (paymentMethod) => {
        setSelectedPayment(paymentMethod)
    }

    const handleCompleteOrder = () => {
        if (!selectedPayment || submitLoading) return

        const storedOrderId = localStorage.getItem("selfOrderId")
        const resolvedOrderId =
            selfSingleOrderInfo?._id ||
            (storedOrderId === "null" || storedOrderId === null ? null : storedOrderId)

        // Pay at Counter / Scan (SELF): no in-app payment is taken.
        // Mark the order self-completed so the cashier's web-billing notification
        // fires and they can settle it at the counter.
        if (selectedPayment?.type === 'counter' || selectedPayment?.type === 'scan') {
            payAtCounterSelfOrderAPI({
                orderId: resolvedOrderId,
                branchId: userBranchId,
                isPayAtCounter: true,
                password: ""
            })
            return
        }

        // Real payment (STAFF, or SELF choosing UPI/Card) -> pos/postPosPayment
        const paymentMethodBody = [{
            type: selectedPayment?.type || 'cash',
            journalId: selectedPayment?.journalId || selectedPayment?._id || null,
            paidAmt: Number(totalAmount),
            cardId: null
        }]

        const payload = {
            orderId: resolvedOrderId,
            branchId: userBranchId,
            discountAmt: 0,
            deliveryCharge: 0,
            pkgCharge: 0,
            amtTotal: Number(Number(totalAmount).toFixed(decimalPosition)),
            paymentDate: `${today} ${getCurrentTime()}`,
            paymentMethod: paymentMethodBody,
            cusId: selfSingleOrderInfo?.cusId || defaultCustomer?._id || null,
            password: "",
            customerMobile: selfSingleOrderInfo?.customerMobile || null,
            customerName: selfSingleOrderInfo?.customerName || 'Guest',
            roundOff: 0
        }
        completeSelfOrderAPI(payload)
    }

    const totalAmount = receiptData?.amtTotal || selfSingleOrderInfo?.amtTotal || 0

    return (
        <div className='self-payment-container'>
            <div className='self-order-middle-container' style={{ placeItems: "center" }}>
                <div className='self-payment-head' style={{ marginBottom: "20px" }}>
                    <h3>SELECT PAYMENT METHOD</h3>
                    <p className='self-payment-total'>
                        Total: <strong>{formatCurrencyValue(totalAmount, currencyDetails, decimalPosition)}</strong>
                    </p>
                </div>
                <div className={`self-payment-main ${isStaff ? 'staff-view' : ''}`}>
                    {/* Pay at Counter - Self customers only */}
                    {!isStaff && (
                    <div 
                        className={`self-payment-box ${selectedPayment?.type === 'counter' ? 'selected' : ''}`}
                        onClick={() => handlePaymentSelect({ type: 'counter', journalName: 'Pay at Counter' })}
                    >
                        <div className='self-payment-image'>
                            <img src={counter} alt='Counter' />
                        </div>
                        <div className='self-payment-content'>
                            <h1>PAY AT COUNTER</h1>
                            <h2>Please take your receipt to the cashier</h2>
                        </div>
                    </div>
                    )}

                    {/* Dynamic payment methods from branch settings (UPI / Card / Cash etc.) */}
                    {paymentTypeList && paymentTypeList.length > 0 && paymentTypeList.map((method, index) => (
                        <div 
                            key={method._id || index}
                            className={`self-payment-box ${selectedPayment?._id === method._id ? 'selected' : ''}`}
                            onClick={() => handlePaymentSelect(method)}
                        >
                            <div className='self-payment-image self-payment-icon'>
                                {getPaymentIcon(method.journalName)}
                            </div>
                            <div className='self-payment-content'>
                                <h1>{(method.journalName || 'Payment').toUpperCase()}</h1>
                                <h2>
                                    {method.journalName?.toLowerCase().includes('card') 
                                        ? 'Pay using Credit/Debit Card' 
                                        : method.journalName?.toLowerCase().includes('upi')
                                        ? 'Pay using UPI / QR Code'
                                        : 'Complete payment'}
                                </h2>
                            </div>
                        </div>
                    ))}

                    {/* Fallback if no payment methods configured in branch settings */}
                    {(!paymentTypeList || paymentTypeList.length === 0) && (
                        isStaff ? (
                            <div className='self-payment-box self-payment-empty'>
                                <div className='self-payment-content'>
                                    <h1>NO PAYMENT METHODS</h1>
                                    <h2>Configure payment types in branch settings</h2>
                                </div>
                            </div>
                        ) : (
                            <div 
                                className={`self-payment-box ${selectedPayment?.type === 'scan' ? 'selected' : ''}`}
                                onClick={() => handlePaymentSelect({ type: 'scan', journalName: 'Scan and Pay' })}
                            >
                                <div className='self-payment-image'>
                                    <img src={payment} alt='Scan' />
                                </div>
                                <div className='self-payment-content'>
                                    <h1>SCAN AND PAY</h1>
                                    <h2>Scan the QR code to pay</h2>
                                </div>
                            </div>
                        )
                    )}
                </div>

                {/* Action Buttons */}
                <div className='self-payment-actions'>
                    <button 
                        className='self-payment-btn back-btn'
                        onClick={backBtnFunction}
                    >
                        BACK
                    </button>
                    <button 
                        className={`self-payment-btn confirm-btn ${!selectedPayment ? 'disabled' : ''}`}
                        onClick={handleCompleteOrder}
                        disabled={!selectedPayment || submitLoading}
                    >
                        {submitLoading ? <CircularProgress size={20} color="inherit" /> : 'CONFIRM PAYMENT'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default SelfPayment